import { z } from 'zod'
import type { Erc20Transfer } from '../../types'
import { bigintTextSchema, lowerHexSchema } from '../../types/primitive'
import type { TransactionIndex } from '../../types/transactionIndex'
import type { EtherscanClient } from './client'

type GetListOfErc20TokenTransferEventsByAddressParameters = {
  client: EtherscanClient
  address: string
  startblock?: number
  endblock?: number
  page?: number
  offset?: number
  sort?: 'asc' | 'desc'
  headers?: Record<string, string>
}

const transferSchema = z.object({
  blockNumber: z.string().regex(/^\d+$/).transform(Number),
  hash: lowerHexSchema,
  timeStamp: z.string().regex(/^\d+$/).transform(Number),
  contractAddress: lowerHexSchema,
  from: lowerHexSchema,
  to: lowerHexSchema,
  value: bigintTextSchema,
})
const resultSchema = z.array(transferSchema)

/** {@link https://docs.etherscan.io/api-endpoints/accounts#get-a-list-of-erc20-token-transfer-events-by-address} */
export const getListOfErc20TokenTransferEventsByAddress = async ({
  client,
  address,
  startblock = 0,
  endblock,
  page = 1,
  offset = 10_000,
  sort = 'asc',
  headers,
}: GetListOfErc20TokenTransferEventsByAddressParameters): Promise<{
  indexes: readonly TransactionIndex[]
  erc20Transfers: readonly Erc20Transfer[]
}> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'account',
        action: 'tokentx',
        address,
        startblock: startblock.toString(),
        ...(endblock === undefined
          ? {}
          : {
              endblock: endblock.toString(),
            }),
        page: page.toString(),
        offset: offset.toString(),
        sort,
      },
      resultSchema,
      headers,
    })
    const indexes: TransactionIndex[] = []
    const transfers: Erc20Transfer[] = []
    for (const data of result) {
      indexes.push({
        blockNumber: data.blockNumber,
        hash: data.hash,
        timestamp: data.timeStamp * 1000,
      })
      transfers.push({
        address: data.contractAddress,
        from: data.from,
        logIndex: 0,
        to: data.to,
        value: data.value,
      })
    }
    return {
      indexes,
      erc20Transfers: transfers,
    }
  } catch (error) {
    if (error instanceof Error && error.message === 'No transactions found') {
      return {
        indexes: [],
        erc20Transfers: [],
      }
    }
    throw error
  }
}
